import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { VideoPlayer } from '../../components/Molecules';
import { DiscardChangePopup } from '../../components/Popups';
import { CreateMemoryGameContentListTemplate } from '../../components/Templates';
import { getLanguageDetail } from '../../constants/languageDetails';
import { AppToastInfoOption } from '../../constants/toast';
import {
  getBibleBoostCreateVideo,
  getMemoryBoostCreateVideo,
} from '../../constants/videos';
import { usePopup } from '../../context';
import { TranslationLocale } from '../../i18n';
import { QuickCreateGameMode } from '../../models/DraftStore';
import { WebBoosterType } from '../../models/UserStore';
import { useDraftStore } from '../../stores/DraftStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';
import { getUiLocale } from '../../utils/i18n';
import { isNotEmptyString } from '../../utils/string';

interface State {
  createType: QuickCreateGameMode | undefined;
  gameContents: {
    question: string;
    answer: string;
    questionPlaceHolder: string;
    answerPlaceHolder: string;
    questionHasTts: boolean;
    questionSoundText: string;
    answerHasTts: boolean;
    answerMissingStrokes: string[];
  }[];
  isNextDisabled: boolean;
  isExceedLimit: boolean;
  showRequiredWarning: boolean;
  isFlashing: boolean;
}

const CreateMemoryGameContentListScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();

  const [state, setState] = useState<State>({
    createType: undefined,
    gameContents: [],
    isNextDisabled: false,
    isExceedLimit: false,
    showRequiredWarning: false,
    isFlashing: false,
  });

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const userAccount = useUserStore((store) =>
    store.getAccount(activeUserId ?? ''),
  );

  const { mode, locale } = useDraftStore((store) => {
    const _mode = store.getGameMode();
    const _locale = store.getLocale();
    return { mode: _mode, locale: _locale };
  });

  const { lastEditIndex, setLastEditIndex } = useDraftStore();

  const timeoutRef = useRef<number | null>(null);

  const drafts = useDraftStore((store) => {
    return store.getGameDrafts();
  }, arrayEqual(shallow));

  useEffect(() => {
    if (mode) {
      setState((s) => ({ ...s, createType: mode }));
    } else {
      navigate('/create/memory-game/select-type', { replace: true });
    }
  }, [mode, navigate]);

  useEffect(() => {
    if (locale) {
      setState((s) => ({ ...s, selectedLanguage: locale }));
    }
  }, [locale]);

  useEffect(() => {
    if (drafts && drafts.length > 0) {
      const gameContents = drafts.map((draft) => {
        return {
          question: draft.game.question.text?.text ?? '',
          answer: draft.game.answer.text?.text ?? '',
          questionPlaceHolder: `<${t(
            getLanguageDetail(draft.game.question.locale)?.displayName ?? '',
            { lng: draft.game.question.locale },
          )}>\n${t(draft.game.question.text?.placeholder ?? '', {
            lng: draft.game.question.locale,
          })}`,
          answerPlaceHolder: `<${t(
            getLanguageDetail(draft.game.answer.locale)?.displayName ?? '',
            { lng: draft.game.answer.locale },
          )}>\n${t(draft.game.answer.text?.placeholder ?? '', {
            lng: draft.game.answer.locale,
          })}`,
          questionHasTts: draft.game.question.hasTts,
          questionSoundText: draft.game.question.text.soundText ?? '',
          answerHasTts: draft.game.answer.hasTts,
          answerMissingStrokes: draft.game.answer.text.missingStrokes,
        };
      });
      setState((s) => ({ ...s, gameContents: gameContents }));
    }
  }, [drafts, drafts.length, t]);

  const onClickRemove = useCallback(
    (index: number) => {
      useDraftStore
        .getState()
        .removeGameDraft(index, t, userAccount?.settings.webBoosterType);
    },
    [t, userAccount?.settings.webBoosterType],
  );

  const onClickAddContent = useCallback(() => {
    useDraftStore
      .getState()
      .addGameDraft(t, userAccount?.settings.webBoosterType);
    setLastEditIndex(state.gameContents.length);
    navigate('/create/memory-game/edit-game', {
      state: {
        index: state.gameContents.length,
        type: 'Answer',
      },
    });
  }, [
    navigate,
    setLastEditIndex,
    state.gameContents.length,
    t,
    userAccount?.settings.webBoosterType,
  ]);

  const onClickNext = useCallback(() => {
    const hasEmpty = state.gameContents.some(
      ({ question, answer }) =>
        !isNotEmptyString(question) || !isNotEmptyString(answer),
    );
    if (hasEmpty) {
      setState((s) => ({ ...s, showRequiredWarning: true }));
    } else {
      setState((s) => ({ ...s, showRequiredWarning: false }));
      useDraftStore.getState().convertDraftsToSelectWords();
      navigate('/create/memory-game/select-words');
      setTimeout(() => {
        document.documentElement.style.scrollBehavior = 'auto';
        document.body.style.scrollBehavior = 'auto';
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 10);
    }
  }, [navigate, state.gameContents]);

  const onClickEdit = useCallback(
    (index: number, type: string) => {
      setLastEditIndex(index);
      navigate('/create/memory-game/edit-game', {
        state: { index: index, type: type },
      });
    },
    [navigate, setLastEditIndex],
  );

  const onDiscard = useCallback(() => {
    useDraftStore.getState().clearGameDraft();
    navigate(-1);
  }, [navigate]);

  const onClickBack = useCallback(() => {
    showPopup(
      <DiscardChangePopup
        onDiscardChange={() => {
          hidePopup();
          onDiscard();
        }}
        onPrimary={hidePopup}
      />,
    );
  }, [hidePopup, onDiscard, showPopup]);

  const exceedContentWarning = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }

    setState((s) => ({
      ...s,
      isFlashing: true,
    }));

    timeoutRef.current = window.setTimeout(() => {
      setState((s) => ({
        ...s,
        isFlashing: false,
      }));
    }, 3000);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
      e.preventDefault();
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (state.gameContents.length > 20) {
      setState((s) => ({ ...s, isNextDisabled: true, isExceedLimit: true }));
      exceedContentWarning();
    } else {
      setState((s) => ({ ...s, isNextDisabled: false, isExceedLimit: false }));
    }
  }, [exceedContentWarning, locale, state.gameContents]);

  const onClickAudioBar = useCallback(() => {
    toast(t(`Listen to the voice (Text-to-speech) in the practice.`), {
      ...AppToastInfoOption,
      id: 'tts',
    });
  }, [t]);

  const [, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
    // Attempt scroll multiple times
    const scrollAttempts = [0, 100, 500]; // Try immediately, after 100ms, and after 500ms

    scrollAttempts.forEach((delay) => {
      setTimeout(() => {
        if (lastEditIndex !== undefined) {
          const element = document.getElementById(`game-card-${lastEditIndex}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, delay);
    });
    return () => setIsMounted(false);
  }, [lastEditIndex]);

  const expectedPayment = useMemo(() => {
    const gameNum = state.gameContents.filter(
      (gameContent) => gameContent.answer.trim() !== '',
    ).length;
    return (
      gameNum * (state.createType === QuickCreateGameMode.QuickMemory ? 3 : 2)
    );
  }, [state.gameContents, state.createType]);

  const onClickHelpVideo = useCallback(() => {
    const getVideo =
      userAccount?.settings.webBoosterType === WebBoosterType.Normal
        ? getMemoryBoostCreateVideo
        : getBibleBoostCreateVideo;
    showPopup(
      <VideoPlayer
        url={getVideo(getUiLocale(i18n.language as TranslationLocale))}
        playing={true}
        muted={false}
        // playIcon={<></>}
      />,
      {
        maxWidth: '100%',
        maxHeight: '100%',
        aspectRatio: '16/9',
        withCross: true,
      },
    );
  }, [i18n.language, showPopup, userAccount?.settings.webBoosterType]);

  return (
    <>
      <CreateMemoryGameContentListTemplate
        type={state.createType}
        webBoosterType={userAccount?.settings.webBoosterType}
        gameContents={state.gameContents}
        isNextDisabled={state.isNextDisabled}
        isExceedLimit={state.isExceedLimit}
        showRequiredWarning={state.showRequiredWarning}
        isFlashing={state.isFlashing}
        expectedPayment={expectedPayment}
        onClickHelpVideo={onClickHelpVideo}
        onClickEdit={onClickEdit}
        onClickRemove={onClickRemove}
        onClickAddContent={onClickAddContent}
        onClickAudioBar={onClickAudioBar}
        onClickNext={onClickNext}
        onClickBack={onClickBack}
      />
    </>
  );
};
export default CreateMemoryGameContentListScreen;
